import { createFileRoute, Link } from "@tanstack/react-router";
import { useLoaderData } from "@tanstack/react-router";
import { BookOpen, Clock } from "lucide-react";
import { useLocale } from "@/lib/i18n/locale-context";
import { getPageCopy } from "@/lib/i18n/page-copy";
import { localizedPath } from "@/lib/i18n/slug-map";

const TITLE = "Guider om att köpa och sälja på nätet | Sellavo";
const DESCRIPTION =
  "Praktiska guider om att sälja begagnat på nätet: prissättning, bilder, annonstext, frakt och trygga affärer – och hur du köper begagnat utan att bli lurad.";

export const Route = createFileRoute("/guider/")({
  // Guidetexterna laddas som eget paket bara för den här sidan.
  loader: async () => {
    const { GUIDES } = await import("@/lib/guides");
    return {
      guides: GUIDES.map((g) => ({
        slug: g.slug,
        title: g.title,
        description: g.description,
        readMinutes: g.readMinutes,
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sellavo.com/guider" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://sellavo.com/guider" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Guider om att köpa och sälja på nätet",
          itemListElement: (loaderData?.guides ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `https://sellavo.com/guider/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: GuidesHub,
});

export function GuidesHub() {
  const { guides } = useLoaderData({ strict: false }) as { guides: { slug: string; title: string; description: string; readMinutes: number }[] };
  const { lang } = useLocale();
  const copy = lang === "sv" ? null : getPageCopy(lang).guides;
  return (
    <main className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <p className="font-heading text-[11px] font-bold uppercase tracking-wide text-primary">
        {copy?.eyebrow ?? "Guider"}
      </p>
      <h1 className="mt-2 font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl">
        {copy?.title ?? "Köpa och sälja på nätet"}
      </h1>
      <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
        {copy?.intro ?? "Allt vi lärt oss om att få prylar sålda: vad de är värda, hur du fotar dem, vad annonsen ska innehålla och hur du gör affären trygg för både dig och köparen."}
      </p>

      <p className="mt-4 text-sm text-muted-foreground">
        {copy?.also ?? "Se också"}{" "}
        <Link to={localizedPath("/kopa-och-salja", lang)} className="text-primary hover:underline">
          {copy?.local ?? "köpa och sälja på din ort"}
        </Link>{" "}
        {copy?.and ?? "och"}{" "}
        <Link to={localizedPath("/jamfor", lang)} className="text-primary hover:underline">
          {copy?.comparisons ?? "jämförelser med andra säljsajter"}
        </Link>
        .
      </p>

      <ul className="mt-10 grid gap-4 sm:grid-cols-2">
        {guides.map((guide) => (
          <li key={guide.slug}>
            <Link
              to="/guider/$slug"
              params={{ slug: guide.slug }}
              className="flex h-full flex-col rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary/50 sm:p-6"
            >
              <span className="flex items-center gap-2 text-primary">
                <BookOpen className="h-4 w-4 shrink-0" />
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide">
                  <Clock className="h-3 w-3" />
                  {guide.readMinutes} min
                </span>
              </span>
              <h2 className="mt-3 font-heading text-lg font-bold text-foreground">{guide.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {guide.description}
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}
